import { useState } from "react";
import { format, startOfDay } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { RangePreset, RangeSelection } from "@/lib/dashboardDateRange";

const PRESET_BUTTONS: { value: RangePreset; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "month", label: "This month" },
  { value: "1y", label: "1y" },
  { value: "all", label: "All" },
];

interface DateRangeFilterProps {
  preset: RangePreset;
  from: Date;
  to: Date;
  onChange: (selection: RangeSelection) => void;
}

const DateRangeFilter = ({ preset, from, to, onChange }: DateRangeFilterProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) setDraft(preset === "custom" ? { from, to: new Date(to.getTime() - 1) } : undefined);
  };

  // react-day-picker 9 fills `to` on the first click, so a range cannot be
  // applied as soon as both ends are set — the user confirms with Apply.
  const handleApply = () => {
    if (!draft?.from) return;
    onChange({
      preset: "custom",
      customFrom: draft.from,
      customTo: draft.to ?? draft.from,
    });
    setOpen(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {PRESET_BUTTONS.map((item) => (
        <Button
          key={item.value}
          size="sm"
          variant={preset === item.value ? "default" : "outline"}
          onClick={() => onChange({ preset: item.value })}
        >
          {item.label}
        </Button>
      ))}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button size="sm" variant={preset === "custom" ? "default" : "outline"}>
            <CalendarIcon />
            {preset === "custom"
              ? `${format(from, "MMM d")} – ${format(new Date(to.getTime() - 1), "MMM d")}`
              : "Custom"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            numberOfMonths={2}
            defaultMonth={draft?.from ?? (preset === "all" ? new Date() : from)}
            selected={draft}
            onSelect={setDraft}
            disabled={{ after: startOfDay(new Date()) }}
          />
          <div className="flex justify-end border-t p-2">
            <Button size="sm" disabled={!draft?.from} onClick={handleApply}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateRangeFilter;
